import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Overline, Section, Pill } from "../components/Bits";
import { ShieldCheck, Soup, Sparkles, Clock, Users, GraduationCap, Building2, PartyPopper, Trophy, Sprout, ArrowRight } from "lucide-react";
import { EVENT_TYPES } from "../lib/gomill-data";
import { BRAND } from "../lib/gomill-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = [
  { label: "Up to 100", icon: Users },
  { label: "101–250", icon: Users },
  { label: "251–500", icon: Users },
  { label: "501–1000", icon: Users },
  { label: "1000+", icon: Users },
];

const OCCASIONS = [
  { icon: Building2, t: "Office Lunch Spot" },
  { icon: GraduationCap, t: "Colleges & Universities" },
  { icon: Sprout, t: "Apartment Activation" },
  { icon: PartyPopper, t: "Parties & Celebrations" },
  { icon: Trophy, t: "Sports & Tournaments" },
  { icon: Sparkles, t: "Festivals & Communities" },
];

const initial = {
  name: "", phone: "", email: "", event_type: EVENT_TYPES[0],
  event_date: "", event_location: "", expected_guests: "", preferred_time: "", message: "",
};

export default function BookEvent() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry/event`, form);
      toast.success("Enquiry sent! Our team will reach out within 30 minutes.");
      setForm(initial);
    } catch (err) {
      toast.error("Couldn't submit. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Overline className="mb-4">Book GoMill for Events</Overline>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-gomill-green leading-[1.02] tracking-tight">
              Great food. <span className="italic">Anywhere.</span> Any occasion.
            </h1>
            <p className="mt-5 text-lg text-gomill-muted max-w-xl">Bring the GoMill experience to your people. Fresh, guilt-free meals. Live. On-site. Hassle-free.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Pill tone="leaf">5000+ Events Served</Pill>
              <Pill tone="mustard">4.8★ Customer Rating</Pill>
              <Pill tone="cream">Pan-India</Pill>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[5/4] rounded-[32px] overflow-hidden bg-gomill-cream">
              <img src={BRAND.truckCity} alt="GoMill event truck" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </Section>

      {/* Occasions */}
      <Section className="bg-gomill-cream rounded-3xl mx-3 md:mx-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Overline className="mb-3">Perfect for every occasion</Overline>
          <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">From boardroom to bonfire.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {OCCASIONS.map((o) => (
            <div key={o.t} className="bg-white rounded-3xl p-6 border border-gomill-border hover:border-gomill-green hover:-translate-y-1 transition-all">
              <div className="w-12 h-12 rounded-2xl bg-gomill-mustard/30 flex items-center justify-center mb-4">
                <o.icon className="w-5 h-5 text-gomill-green" />
              </div>
              <div className="font-serif text-lg text-gomill-green">{o.t}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Event size */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-10">
          <div className="md:col-span-7">
            <Overline className="mb-3">Choose Your Event Size</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">100 or 10,000 — we scale with you.</h2>
          </div>
          <p className="md:col-span-5 text-gomill-muted text-lg">Compact kiosks for intimate events, multi-truck setups for festivals.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {SIZES.map((s) => (
            <div key={s.label} className="bg-white border border-gomill-border rounded-3xl p-5 text-center hover:border-gomill-green transition-colors">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-gomill-cream flex items-center justify-center mb-3">
                <s.icon className="w-5 h-5 text-gomill-leaf" />
              </div>
              <div className="font-serif text-lg text-gomill-green">{s.label}</div>
              <div className="text-xs text-gomill-muted mt-1">guests</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Form */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-3">Tell us your plan</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">Book your GoMill truck.</h2>
            <p className="mt-4 text-gomill-muted text-lg max-w-md">Our team will call you within 30 minutes with a custom proposal — menu, setup and pricing.</p>
            <ul className="mt-8 space-y-4">
              {[
                { i: Soup, t: "Live fresh cooking, on-site." },
                { i: ShieldCheck, t: "Branded hygiene, every event." },
                { i: Clock, t: "On-time setup, every time." },
                { i: Sparkles, t: "Customisable millet-led menus." },
              ].map((b) => (
                <li key={b.t} className="flex items-start gap-3 text-gomill-green">
                  <span className="w-9 h-9 rounded-xl bg-gomill-mustard/30 flex items-center justify-center"><b.i className="w-4 h-4" /></span>
                  <span className="text-gomill-text">{b.t}</span>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-white border border-gomill-border rounded-3xl p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="event-form">
            <Field label="Your Name" name="name" value={form.name} onChange={update("name")} required />
            <Field label="Phone Number" name="phone" value={form.phone} onChange={update("phone")} required />
            <Field label="Email Address" name="email" type="email" value={form.email} onChange={update("email")} required />
            <SelectField label="Event Type" name="event_type" value={form.event_type} onChange={update("event_type")} options={EVENT_TYPES} />
            <Field label="Event Date" name="event_date" type="date" value={form.event_date} onChange={update("event_date")} />
            <Field label="Event Location" name="event_location" value={form.event_location} onChange={update("event_location")} required />
            <Field label="Expected Guests" name="expected_guests" value={form.expected_guests} onChange={update("expected_guests")} required placeholder="e.g. 200" />
            <Field label="Preferred Time" name="preferred_time" type="time" value={form.preferred_time} onChange={update("preferred_time")} />
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest font-bold text-gomill-leaf mb-2">Anything else?</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={update("message")}
                data-testid="event-message"
                className="w-full rounded-2xl border border-gomill-border bg-gomill-cream/60 px-4 py-3 text-gomill-green focus:outline-none focus:ring-2 focus:ring-gomill-mustard"
                placeholder="Tell us about the event — menu preferences, dietary needs..."
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="event-submit-btn"
              className="md:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gomill-mustard hover:bg-gomill-mustardDark text-gomill-green font-bold shadow-sm disabled:opacity-60"
            >
              {loading ? "Submitting…" : <>Submit Enquiry <ArrowRight className="w-4 h-4"/></>}
            </button>
          </form>
        </div>
      </Section>
    </>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest font-bold text-gomill-leaf mb-2">{label}</span>
      <input
        data-testid={`field-${props.name}`}
        {...props}
        className="w-full rounded-2xl border border-gomill-border bg-gomill-cream/60 px-4 py-3 text-gomill-green focus:outline-none focus:ring-2 focus:ring-gomill-mustard"
      />
    </label>
  );
}

function SelectField({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest font-bold text-gomill-leaf mb-2">{label}</span>
      <select
        data-testid={`field-${props.name}`}
        {...props}
        className="w-full rounded-2xl border border-gomill-border bg-gomill-cream/60 px-4 py-3 text-gomill-green focus:outline-none focus:ring-2 focus:ring-gomill-mustard"
      >
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
